import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ServiceType, Visit } from '../data/types';
import { mockSnapshot } from '../data/api';
import { fetchRealDay, type RealDay } from '../data/realDay';
import type { Snapshot } from '../lib/scheduling';

export type AreaId = 'inicio' | 'agenda' | 'espacio' | 'colores' | `soon:${string}`;
export type AgendaMode = 'calendario' | 'envivo' | 'estado' | 'salud';
export type ColorBy = 'team' | 'service';
export type DataSource = 'real' | 'demo';
export interface RealState { status: 'idle' | 'loading' | 'ok' | 'error'; day?: RealDay; zone?: string }

export const SERVICE_LABEL: Record<ServiceType, string> = {
  regular: 'Regular', profunda: 'Profunda', oficina: 'Oficina', mudanza: 'Mudanza',
};

export const PALETTE = ['#2F6FDE', '#8E44AD', '#0F8C7B', '#C2255C', '#E36414', '#5C7C1A', '#3A3A3D', '#0E7490'];

const AREA_LABEL: Record<string, string> = {
  inicio: 'Inicio', agenda: 'Agenda', espacio: '¿Hay espacio?', colores: 'Colores',
};

export interface ComingSoonInfo { title: string; body: string }

interface Store {
  snapshot: Snapshot;
  addVisit(v: Visit): void;
  updateVisit(uid: string, patch: Partial<Visit>): void;
  colorBy: ColorBy;
  setColorBy(c: ColorBy): void;
  teamColors: Record<string, string>;
  setTeamColor(id: string, c: string): void;
  serviceColors: Record<ServiceType, string>;
  setServiceColor(s: ServiceType, c: string): void;
  /** Color de un trabajo según la preferencia del dueño. */
  colorFor(v: { teamId: string; service: ServiceType }): string;
  open: AreaId[];
  active: AreaId;
  go(a: AreaId): void;
  close(a: AreaId): void;
  areaLabel(a: AreaId): string;
  agendaMode: AgendaMode;
  setAgendaMode(m: AgendaMode): void;
  soon: ComingSoonInfo | null;
  showSoon(info: ComingSoonInfo | null): void;
  toast: string | null;
  notify(msg: string): void;
  /** Datos prellenados al abrir ¿Hay espacio? desde una llamada. */
  caller: { name: string; phone?: string; address?: string } | null;
  setCaller(c: Store['caller']): void;
  /** Calendario: visitas reales de la empresa (solo lectura) o datos de ejemplo. */
  dataSource: DataSource;
  setDataSource(d: DataSource): void;
  real: RealState;
  reloadReal(): void;
  /** Aviso cuando la empresa no tiene zona horaria y se usa la del navegador. */
  zoneNote?: string;
}

const Ctx = createContext<Store | null>(null);

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}
function save(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* sin almacenamiento: solo en memoria */ }
}

export function StoreProvider({ children, routeBase, zone, zoneNote }: { children: ReactNode; routeBase: string; zone?: string; zoneNote?: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  // TODO(API): replace this complete synthetic snapshot only when team membership,
  // capacity and break data can be read together with visits for the active company.
  // Mixing real visits with the demo's Brisa teams would invent availability.
  const [visits, setVisits] = useState<Visit[]>(mockSnapshot.visits);
  const [colorBy, setColorByS] = useState<ColorBy>(() => load('fn.colorBy', 'team'));
  const [teamColors, setTeamColors] = useState<Record<string, string>>(() =>
    load('fn.teamColors', Object.fromEntries(mockSnapshot.teams.map(t => [t.id, t.color]))));
  const [serviceColors, setServiceColors] = useState<Record<ServiceType, string>>(() =>
    load('fn.serviceColors', { regular: '#2F6FDE', profunda: '#8E44AD', oficina: '#0F8C7B', mudanza: '#C2255C' }));
  const [agendaMode, setAgendaMode] = useState<AgendaMode>('calendario');
  const [soon, showSoon] = useState<ComingSoonInfo | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [caller, setCaller] = useState<Store['caller']>(null);
  const [dataSource, setDataSourceS] = useState<DataSource>(() => load('fn.dataSource', 'real'));
  // Con datos reales se entra directo a Agenda: es la única pantalla que ya usa datos reales.
  const [open, setOpen] = useState<AreaId[]>(() => (dataSource === 'real' ? ['agenda'] : ['inicio', 'agenda']));
  const [active, setActive] = useState<AreaId>(() => (dataSource === 'real' ? 'agenda' : 'inicio'));
  const [real, setReal] = useState<RealState>({ status: 'idle' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (dataSource !== 'real') return undefined;
    let alive = true;
    if (!zone) { setReal({ status: 'error' }); return undefined; }
    setReal(r => ({ status: 'loading', day: r.zone === zone ? r.day : undefined, zone }));
    fetchRealDay(zone)
      .then(day => { if (alive) setReal({ status: 'ok', day, zone }); })
      .catch(() => { if (alive) setReal({ status: 'error', zone }); });
    // Refresca cada 2 minutos mientras la pantalla está abierta (solo lectura).
    const t = window.setInterval(() => setReloadKey(k => k + 1), 120000);
    return () => { alive = false; window.clearInterval(t); };
  }, [dataSource, zone, reloadKey]);

  useEffect(() => {
    const area = decodeURIComponent(location.pathname.slice(routeBase.length).replace(/^\//, '')) as AreaId;
    if (!area) return;
    if (['inicio', 'agenda', 'espacio', 'colores'].includes(area) || area.startsWith('soon:')) {
      setActive(area);
      setOpen(items => items.includes(area) ? items : [...items, area]);
    }
  }, [location.pathname, routeBase]);

  const snapshot = useMemo<Snapshot>(() => ({
    today: mockSnapshot.today, now: mockSnapshot.now,
    teams: mockSnapshot.teams.map(t => ({ ...t, color: teamColors[t.id] ?? t.color })),
    visits, blocks: mockSnapshot.blocks, clients: mockSnapshot.clients, closedDays: mockSnapshot.closedDays,
  }), [visits, teamColors]);

  const go = useCallback((a: AreaId) => {
    setOpen(o => (o.includes(a) ? o : [...o, a]));
    setActive(a);
    navigate(`${routeBase}/${a}${location.search}`);
  }, [navigate, routeBase, location.search]);
  const close = useCallback((a: AreaId) => {
    setOpen(o => {
      const next = o.filter(x => x !== a);
      return next.length ? next : ['inicio'];
    });
    if (active === a) {
      const next = open.filter(x => x !== a);
      const target = next[next.length - 1] ?? 'inicio';
      setActive(target);
      navigate(`${routeBase}/${target}${location.search}`);
    }
  }, [active, open, navigate, routeBase, location.search]);
  const notify = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  const value: Store = {
    snapshot,
    addVisit: v => setVisits(vs => [...vs, v]),
    updateVisit: (uid, patch) => setVisits(vs => vs.map(v => (v.uid === uid ? { ...v, ...patch } : v))),
    colorBy,
    setColorBy: c => { setColorByS(c); save('fn.colorBy', c); },
    teamColors,
    setTeamColor: (id, c) => setTeamColors(m => { const n = { ...m, [id]: c }; save('fn.teamColors', n); return n; }),
    serviceColors,
    setServiceColor: (s, c) => setServiceColors(m => { const n = { ...m, [s]: c }; save('fn.serviceColors', n); return n; }),
    colorFor: v => (colorBy === 'team' ? teamColors[v.teamId] : serviceColors[v.service]) ?? '#8A8B8E',
    open, active, go, close,
    areaLabel: a => (a.startsWith('soon:') ? a.slice(5) : AREA_LABEL[a] ?? a),
    agendaMode, setAgendaMode,
    soon, showSoon, toast, notify, caller, setCaller,
    dataSource,
    setDataSource: d => { setDataSourceS(d); save('fn.dataSource', d); },
    real,
    reloadReal: () => setReloadKey(k => k + 1),
    zoneNote,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore(): Store {
  const s = useContext(Ctx);
  if (!s) throw new Error('useStore fuera de StoreProvider');
  return s;
}

/** Fondo suave a partir de un color de equipo/servicio. */
export function tint(hex: string, alpha = 0.14): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
